/**
 * Admin Login Page
 * 
 * Login page for admin dashboard with professional centered card design.
 * 
 * @version 2.0.0
 */

import React, { useState } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import logger from '../logger';
import { validateEmail } from '../validators';
import authService from '../services/authService';

/**
 * AdminLoginPage Component
 * Handles admin login with email and password
 */
const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    console.log('🔐 Login attempt started');
    setError('');
    setSuccess('');

    // Validation
    if (!email || !password) {
      console.log('❌ Validation: Missing credentials');
      setError('Please enter both email and password');
      return;
    }

    if (!validateEmail(email)) {
      console.log('❌ Validation: Invalid email format');
      setError('Please enter a valid email address');
      return;
    }

    if (password.length < 6) {
      console.log('❌ Validation: Password too short');
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    console.log('⏳ Loading state set to true');

    try {
      console.log('📡 Calling authService.login with email:', email);
      // Call real authentication API
      const response = await authService.login(email, password);

      console.log('✅ Login response:', response);
      if (response && response.success) {
        setSuccess('Login successful! Redirecting...');
        logger.info('Admin login successful', { email });

        // Redirect to dashboard after 1 second
        setTimeout(() => {
          console.log('🔄 Navigating to dashboard');
          navigate('/admin/dashboard');
        }, 1000);
      }
    } catch (err) {
      console.log('❌ Login error caught:', err);
      const errorMessage = err?.message || 'Login failed. Please try again.';
      console.log('🔴 Error message set to:', errorMessage);
      logger.error('Admin login error', { error: errorMessage, email });
      setError(errorMessage);
      setLoading(false);
      console.log('⏹️  Loading state set to false');
    }
  };

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        py: 2,
      }}
    >
      <Container maxWidth="xs" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper
          elevation={6}
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'white',
            position: 'relative',
            width: '100%',
          }}
        >
          {/* Top Colored Bar */}
          <Box
            sx={{
              height: '6px',
              background: 'linear-gradient(135deg, #1e5f3d 0%, #2c7d4f 100%)',
              width: '80px',
              mx: 'auto',
              display: 'block',
              mt: 0,
            }}
          />

          {/* Content */}
          <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            {/* Logo Circle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 80,
                  width: 80,
                  borderRadius: '50%',
                  background: '#f0f0f0',
                  border: '2px solid #e8e8e8',
                }}
              >
                <img
                  src="/logo.jpeg"
                  alt="Roots & Wings Logo"
                  style={{
                    height: 70,
                    width: 70,
                  }}
                />
              </Box>
            </Box>

            {/* Heading */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: '700',
                color: '#1a1a1a',
                mb: 1,
                fontSize: '1.5rem',
              }}
            >
              Welcome back!
            </Typography>

            {/* Subheading */}
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 3,
                fontSize: '0.95rem',
              }}
            >
              Sign in to access your admin dashboard
            </Typography>

            {/* Alert Messages */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2.5,
                  borderRadius: '8px',
                  backgroundColor: '#fef3f2',
                  color: '#d32f2f',
                  border: '1px solid #fecdd3',
                  '& .MuiAlert-icon': { color: '#d32f2f' }
                }}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2.5,
                  borderRadius: '8px',
                  backgroundColor: '#f0fdf4',
                  color: '#059669',
                  border: '1px solid #dcfce7',
                  '& .MuiAlert-icon': { color: '#059669' }
                }}
              >
                {success}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleLogin} sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                margin="normal"
                placeholder="you@example.com"
                variant="outlined"
                InputProps={{
                  sx: {
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    backgroundColor: '#f5f5f5',
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    border: '1px solid #e0e0e0',
                    '&:hover fieldset': {
                      borderColor: '#1e5f3d',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e5f3d',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#666',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1e5f3d',
                  }
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                margin="normal"
                placeholder="••••••••"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePassword}
                        edge="end"
                        disabled={loading}
                        sx={{ 
                          color: '#1e5f3d',
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    backgroundColor: '#f5f5f5',
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    border: '1px solid #e0e0e0',
                    '&:hover fieldset': {
                      borderColor: '#1e5f3d',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e5f3d',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#666',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1e5f3d',
                  }
                }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  background: '#1e5f3d',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '1rem',
                  py: 1.4,
                  borderRadius: '8px',
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: '#165a35',
                  },
                  '&:disabled': {
                    background: '#ccc',
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1.5, color: 'white' }} />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>

            {/* Footer Security */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mt: 3,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  fontSize: '0.9rem',
                }}
              >
                🔒
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#999',
                  fontSize: '0.85rem',
                }}
              >
                Secured with 256-bit encryption
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminLoginPage;
